import React, { useEffect, useState } from "react";
import { NavLink, matchPath, useLocation } from "react-router-dom";
import { Icon, Shelf, Stack } from "@chaser/components";
import { FeatureGate } from "../FeatureGate";
import { Features, roles, Modules, Integrations } from "../../util/constants";
import useResponsiveValue from "../../hooks/useResponsiveValue";
import useDisplayBanner from "../../hooks/useDisplayBanner";
import useOrganisationInfo from "../../hooks/useOrganisationInfo";
import useOrganisation from "@/hooks/useOrganisation.js";
import { useRouteConfig } from "../../contexts/RouteContext";
import { useSidebar } from "../../contexts/SidebarContext";
import RoleGate from "../FeatureGate/RoleGate";
import ReviewButton from "../OrganisationSideMenu/ReviewButton";
import FakeDoorButton from "../OrganisationSideMenu/FakeDoorButton";
import ModuleGate from "../FeatureGate/ModuleGate.jsx";
import "./OrganisationSideMenu.scss";

const SIDEBAR_COLLAPSE_STORAGE_KEY = "sidebarCollapsed";
const SIDEBAR_OPEN_SECTION_STORAGE_KEY = "sidebarOpenSection";
const MIN_COLLAPSIBLE_BREAKPOINT = 1; // 0 is mobile; keep mobile on responsive bottom-nav pattern.

const isAccordionSection = (section) =>
  ["receivables", "cashflow"].includes(section);

/* Shared chevron used by every accordion trigger */
const AccordionChevron = ({ isOpen }) => (
  <svg
    className={`accordion-chevron ${isOpen ? "open" : ""}`}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 12L12 3l9 9"
      stroke="var(--svg-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9"
      stroke="var(--svg-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const AccessCapitalIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3L2 10h20L12 3z"
      stroke="var(--svg-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 10h18M3 21h18"
      stroke="var(--svg-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M6 10v11M10 10v11M14 10v11M18 10v11"
      stroke="var(--svg-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const MenuLink = ({
  to,
  label,
  trailing,
  activeOn,
  className = "accordion-sub-link",
  ...rest
}) => (
  <NavLink
    to={to}
    className={className}
    isActive={(match, location) => {
      if (match) return true;
      if (activeOn) {
        const activePatterns = Array.isArray(activeOn) ? activeOn : [activeOn];
        const matched = activePatterns.some((pattern) => {
          if (typeof pattern === "string") {
            return !!matchPath(location.pathname, { path: pattern });
          }

          return !!matchPath(location.pathname, pattern);
        });
        if (matched) return true;
      }
      return false;
    }}
    {...rest}
  >
    <span className="accordion-sub-link__content">
      <span>{label}</span>
      {trailing}
    </span>
  </NavLink>
);

const MobileMenuLink = ({ to, label, icon, activeOn, ...rest }) => (
  <NavLink
    to={to}
    exact={to === "/"}
    className="mobile-icon-link"
    aria-label={label}
    isActive={(match, location) => {
      if (match) return true;
      if (activeOn) {
        const activePatterns = Array.isArray(activeOn) ? activeOn : [activeOn];
        return activePatterns.some((pattern) => {
          if (typeof pattern === "string") {
            return !!matchPath(location.pathname, {
              path: pattern,
              exact: pattern === "/",
            });
          }

          return !!matchPath(location.pathname, pattern);
        });
      }
      return false;
    }}
    {...rest}
  >
    {typeof icon === "string" ? <Icon name={icon} size={24} /> : icon}
  </NavLink>
);

/* Map a pathname to its accordion section key */
const getSection = (path) => {
  if (path === "/" || path === "/home") return "home";
  if (path.startsWith("/cashflow/receivables/forecast")) return "receivables";
  if (path.startsWith("/receivables/collections")) return "collections";
  if (
    path.startsWith("/receivables") ||
    path.startsWith("/chaserpay") ||
    path.startsWith("/credit-check") ||
    path.startsWith("/reports")
  )
    return "receivables";
  if (path.startsWith("/cashflow")) return "cashflow";
  if (path.startsWith("/capital")) return "capital";
  return null;
};

const MenuWrapper = ({ mobile, children }) => {
  if (mobile)
    return (
      <Shelf as="nav" gap={0} justifyContent="space-between">
        {children}
      </Shelf>
    );
  return (
    <Stack as="nav" gap={0}>
      {children}
    </Stack>
  );
};

function OrganisationSideMenu({ onCollapseChange, showImport }) {
  const location = useLocation();
  const breakpoint = useResponsiveValue([0, 1, 2]);
  const showTrialBanner = useDisplayBanner();
  const { accountingSoftware } = useOrganisation();
  const { cashFlowIntelligence, isManualCSV } = useOrganisationInfo();
  const { collapseSidebar } = useRouteConfig();
  const { isManuallyCollapsed, setIsManuallyCollapsed } = useSidebar();
  const isCollapsed = isManuallyCollapsed;
  const canCollapse = breakpoint >= MIN_COLLAPSIBLE_BREAKPOINT;

  const cashflowForecastLink = cashFlowIntelligence?.onboardingProcessCompleted
    ? "/cashflow/forecast/table"
    : "/cashflow/forecast/onboarding";

  // Sync collapsed state from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY);
      if (saved) {
        const val = JSON.parse(saved);
        const next = canCollapse ? val : false;
        setIsManuallyCollapsed(next);
        if (onCollapseChange) onCollapseChange(next);
      }
    } catch {
      /* ignore */
    }
  }, [canCollapse, onCollapseChange, setIsManuallyCollapsed]);

  useEffect(() => {
    // Force the responsive pattern on mobile and avoid collapsed width states.
    if (!canCollapse && isCollapsed) {
      setIsManuallyCollapsed(false);
      localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, JSON.stringify(false));
      if (onCollapseChange) onCollapseChange(false);
    }
  }, [canCollapse, isCollapsed, onCollapseChange, setIsManuallyCollapsed]);

  useEffect(() => {
    setIsManuallyCollapsed(canCollapse ? collapseSidebar : false);
  }, [canCollapse, collapseSidebar, setIsManuallyCollapsed]);

  /* Single accordion state – only one section open at a time.
     Persisted between sessions; defaults to none for first-time users. */
  const [openSection, setOpenSection] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_OPEN_SECTION_STORAGE_KEY);
      return isAccordionSection(saved) ? saved : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (openSection) {
      localStorage.setItem(SIDEBAR_OPEN_SECTION_STORAGE_KEY, openSection);
    } else {
      localStorage.removeItem(SIDEBAR_OPEN_SECTION_STORAGE_KEY);
    }
  }, [openSection]);

  const toggleCollapse = () => {
    if (!canCollapse) return;

    const next = !isCollapsed;
    setIsManuallyCollapsed(next);
    localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, JSON.stringify(next));
    if (onCollapseChange) onCollapseChange(next);
  };

  /* Toggle an accordion section; opening one closes the others.
     If the sidebar is collapsed, expand it first and open the section. */
  const toggleSection = (section) => (e) => {
    e.preventDefault();

    if (!isAccordionSection(section)) return;

    if (isCollapsed) {
      setIsManuallyCollapsed(false);
      localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, JSON.stringify(false));
      if (onCollapseChange) onCollapseChange(false);
      setOpenSection(section);
    } else {
      setOpenSection((prev) => (prev === section ? null : section));
    }
  };

  const menuContainerClassName = [
    "organisation-side-menu-container",
    "drm-side-menu-root",
    isCollapsed && "collapsed",
    breakpoint === 0 && "drm-side-menu-mobile",
    showTrialBanner && "drm-side-menu-trial",
  ]
    .filter(Boolean)
    .join(" ");

  const mobileMenuContainerClassName = [
    "organisation-side-menu-container",
    "drm-side-menu-root",
    "drm-side-menu-mobile",
    showTrialBanner && "drm-side-menu-trial",
  ]
    .filter(Boolean)
    .join(" ");

  if (breakpoint === 0) {
    return (
      <div className={mobileMenuContainerClassName}>
        <nav className="drm-mobile-nav" aria-label="Primary navigation">
          <ModuleGate name={Modules.RECEIVABLES}>
            <MobileMenuLink
              to="/"
              label="Home"
              icon={<HomeIcon />}
              activeOn="/home"
              data-testid="navigation-home-mobile"
            />
          </ModuleGate>
          <ModuleGate name={Modules.RECEIVABLES}>
            <MobileMenuLink
              to="/receivables/dashboard"
              label="Receivables"
              icon="receivables"
              activeOn={[
                "/receivables",
                "/chaserpay",
                "/credit-check",
                "/reports",
                "/cashflow/receivables/forecast",
              ]}
              data-testid="navigation-receivables-mobile"
            />
          </ModuleGate>
          <FeatureGate name={Features.COLLECTIONS} renderGate={() => null}>
            <ModuleGate name={Modules.RECEIVABLES}>
              <MobileMenuLink
                to="/receivables/collections"
                label="Collections"
                icon="collections"
                activeOn="/receivables/collections"
                data-testid="navigation-collections-mobile"
              />
            </ModuleGate>
          </FeatureGate>
          {(accountingSoftware === Integrations.XERO ||
            accountingSoftware === Integrations.ACCOUNTSIQ) && (
            <RoleGate
              roles={[roles.cashflowManager, roles.owner, roles.manager]}
            >
              <ModuleGate name={Modules.CASHFLOW}>
                <MobileMenuLink
                  to={cashflowForecastLink}
                  label="Cash flow"
                  icon="cash-flow-forecast"
                  activeOn="/cashflow/forecast"
                  data-testid="navigation-cashflow-mobile"
                />
              </ModuleGate>
            </RoleGate>
          )}
          <MobileMenuLink
            to="/capital"
            label="Access capital"
            icon={<AccessCapitalIcon />}
            activeOn="/capital"
            data-testid="navigation-capital-mobile"
          />
        </nav>
      </div>
    );
  }

  return (
    <div className={menuContainerClassName}>
      <div className="organisation-side-menu">
        {canCollapse && (
          <button
            type="button"
            className="collapse-button"
            onClick={toggleCollapse}
            aria-label={isCollapsed ? "Expand side menu" : "Collapse side menu"}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 3v18" />
              {isCollapsed ? (
                <path d="M14 9l3 3-3 3" />
              ) : (
                <path d="M17 9l-3 3 3 3" />
              )}
            </svg>
          </button>
        )}
        <div className="organisation-side-menu--divider-box"></div>
        <div className="organisation-side-menu--inner">
          <MenuWrapper mobile={breakpoint === 0}>
            {/* ── Home ── */}
            <nav className="organisation-side-menu--nav-box">
              <NavLink
                to="/"
                exact
                className="accordion-trigger"
                isActive={(match, loc) =>
                  loc.pathname === "/" || loc.pathname === "/home"
                }
              >
                <div className="organisation-side-menu--inner-link-container">
                  <div className="organisation-side-menu--svg-container">
                    <HomeIcon />
                    {isCollapsed && (
                      <div className="organisation-side-menu--tooltip">
                        Home
                      </div>
                    )}
                  </div>
                  <div className="organisation-side-menu--text">Home</div>
                </div>
              </NavLink>
            </nav>

            {/* ── Receivables ── */}
            <ModuleGate name={Modules.RECEIVABLES}>
              <nav className="organisation-side-menu--nav-box">
                <a
                  href="#"
                  onClick={toggleSection("receivables")}
                  className={`accordion-trigger ${getSection(location.pathname) === "receivables" ? "active" : ""}`}
                >
                  <div className="organisation-side-menu--inner-link-container">
                    <div className="organisation-side-menu--svg-container">
                      <Icon name="receivables" size={24} />
                      {isCollapsed && (
                        <div className="organisation-side-menu--tooltip">
                          Receivables
                        </div>
                      )}
                    </div>
                    <div className="organisation-side-menu--text">
                      Receivables
                    </div>
                    {!isCollapsed && (
                      <AccordionChevron
                        isOpen={openSection === "receivables"}
                      />
                    )}
                  </div>
                </a>
                {!isCollapsed && (
                  <div
                    className={`accordion-sub-items ${openSection === "receivables" ? "open" : ""}`}
                  >
                    <MenuLink
                      to="/receivables/dashboard"
                      label="Dashboard"
                      activeOn={[
                        { path: "/receivables", exact: true },
                        "/receivables/dashboard",
                      ]}
                      data-testid="navigation-dashboard"
                    />
                    <MenuLink
                      to="/receivables/customers"
                      label="Customers and invoices"
                      activeOn={[
                        "/receivables/invoice",
                        "/receivables/invoices",
                        "/receivables/customer",
                        "/receivables/customers",
                      ]}
                      data-testid="navigation-receivables"
                    />
                    <MenuLink
                      data-testid="navigation-tasks"
                      data-trackingid="navigation-tasks"
                      id="tasks-link"
                      to="/receivables/tasks"
                      label="Tasks"
                    />
                    <MenuLink
                      to="/receivables/chaserpay"
                      label="Chaser Pay"
                      data-testid="navigation-chaserpay"
                    />
                    <FeatureGate
                      loadingOverlay
                      name={Features.CREDIT_REPORT}
                      renderGate={() => null}
                    >
                      <MenuLink
                        to="/receivables/credit-check"
                        label="Credit check"
                        data-testid="credit-check"
                      />
                    </FeatureGate>
                    <MenuLink
                      to="/receivables/reports"
                      label="Reports"
                      data-testid="navigation-reports"
                    />
                    <MenuLink
                      to="/receivables/manage"
                      label="Manage"
                      data-testid="navigation-manage"
                    />
                    {showImport && (
                      <MenuLink
                        to={isManualCSV ? "/import/upload" : "/import"}
                        label={isManualCSV ? "Import" : "Manage CSV"}
                        data-testid="navigation-import"
                      />
                    )}
                    <ModuleGate
                      name={Modules.RECEIVABLES}
                      renderGate={() => null}
                    >
                      <MenuLink
                        to="/cashflow/receivables/forecast"
                        label="Receivables forecast"
                        activeOn="/cashflow/receivables/forecast"
                        data-testid="receivables-forecast-manage"
                      />
                    </ModuleGate>
                    <MenuLink
                      to="/receivables/feedback"
                      label="Give feedback"
                      className="accordion-sub-link feedback-link"
                    />
                  </div>
                )}
              </nav>
            </ModuleGate>

            {/* ── Collections ── */}
            <ModuleGate name={Modules.RECEIVABLES} availableToTrial={false}>
              <nav className="organisation-side-menu--nav-box">
                <NavLink
                  to="/receivables/collections"
                  className="accordion-trigger"
                  data-testid="navigation-collections"
                >
                  <div className="organisation-side-menu--inner-link-container">
                    <div className="organisation-side-menu--svg-container">
                      <Icon name="collections" size={24} />
                      {isCollapsed && (
                        <div className="organisation-side-menu--tooltip">
                          Collections
                        </div>
                      )}
                    </div>
                    <div className="organisation-side-menu--text">
                      Collections
                    </div>
                  </div>
                </NavLink>
              </nav>
            </ModuleGate>

            {/* ── Cash flow ── */}
            {(accountingSoftware === Integrations.XERO ||
              accountingSoftware === Integrations.ACCOUNTSIQ) && (
              <RoleGate
                roles={[roles.cashflowManager, roles.owner, roles.manager]}
              >
                <ModuleGate name={Modules.CASHFLOW}>
                  <nav className="organisation-side-menu--nav-box">
                    <a
                      href="#"
                      onClick={toggleSection("cashflow")}
                      className={`accordion-trigger ${getSection(location.pathname) === "cashflow" ? "active" : ""}`}
                    >
                      <div className="organisation-side-menu--inner-link-container">
                        <div className="organisation-side-menu--svg-container">
                          <Icon name="cash-flow-forecast" size={24} />
                          {isCollapsed && (
                            <div className="organisation-side-menu--tooltip">
                              Cash flow
                            </div>
                          )}
                        </div>
                        <div className="organisation-side-menu--text">
                          Cash flow
                        </div>
                        {!isCollapsed && (
                          <AccordionChevron
                            isOpen={openSection === "cashflow"}
                          />
                        )}
                      </div>
                    </a>
                    {!isCollapsed && (
                      <div
                        className={`accordion-sub-items ${openSection === "cashflow" ? "open" : ""}`}
                      >
                        <MenuLink
                          to={cashflowForecastLink}
                          label="Forecast table"
                          activeOn="/cashflow/forecast"
                          data-testid="cash-flow-forecast-manage"
                        />
                        <MenuLink
                          to="/cashflow/feedback"
                          label="Give feedback"
                          className="accordion-sub-link feedback-link"
                          data-testid="cashflow-feedback-link"
                        />
                      </div>
                    )}
                  </nav>
                </ModuleGate>
              </RoleGate>
            )}

            {/* ── Access capital ── */}
            <nav className="organisation-side-menu--nav-box">
              <NavLink
                to="/capital"
                className="accordion-trigger"
                isActive={(match, loc) => loc.pathname.startsWith("/capital")}
                data-testid="navigation-access-capital"
              >
                <div className="organisation-side-menu--inner-link-container">
                  <div className="organisation-side-menu--svg-container">
                    <AccessCapitalIcon />
                    {isCollapsed && (
                      <div className="organisation-side-menu--tooltip">
                        Access capital
                      </div>
                    )}
                  </div>
                  <div className="organisation-side-menu--text">
                    Access capital
                  </div>
                </div>
              </NavLink>
            </nav>

            <FakeDoorButton narrow={breakpoint <= 1 || isCollapsed} />

            {breakpoint > 1 && !isCollapsed && (
              <div className="side-menu-review-wrap">
                <ReviewButton isCollapsed={isCollapsed} />
              </div>
            )}
          </MenuWrapper>
        </div>

        <div className="sidebar-bottom-links">
          {/* Addons */}
          <NavLink
            to="/settings/subscription/addons"
            className="sidebar-bottom-link"
          >
            <div className="organisation-side-menu--svg-container">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              {isCollapsed && (
                <div className="organisation-side-menu--tooltip">Add-ons</div>
              )}
            </div>
            {!isCollapsed && (
              <span className="sidebar-bottom-link--text">Add-ons</span>
            )}
          </NavLink>

          <NavLink
            to="/settings"
            className="sidebar-bottom-link"
            isActive={(match, loc) => loc.pathname.startsWith("/settings")}
          >
            <div className="organisation-side-menu--svg-container">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {isCollapsed && (
                <div className="organisation-side-menu--tooltip">
                  Management
                </div>
              )}
            </div>
            {!isCollapsed && (
              <span className="sidebar-bottom-link--text">Management</span>
            )}
          </NavLink>
        </div>
      </div>
    </div>
  );
}

export default OrganisationSideMenu;
